import type { ApiResponse, LoginResponse, RegisterResponse, TokenPair, UserWithProfile } from '@klaro7/shared-types';

import api, { extractErrorCode, extractErrorMessage } from '@/lib/api';

export interface RegisterPayload {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  phone?: string;
  dateOfBirth: string;
  acceptTerms: boolean;
  acceptPrivacy: boolean;
  marketing?: boolean;
  profiling?: boolean;
  /** Token del widget Cloudflare Turnstile — assente se il widget non è attivo. */
  turnstileToken?: string;
}

export interface UpdateConsentsPayload {
  marketing?: boolean;
  profiling?: boolean;
}

export interface CompleteOnboardingPayload {
  dateOfBirth?: string;
  acceptTerms: boolean;
  acceptPrivacy: boolean;
  marketing?: boolean;
  profiling?: boolean;
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  try {
    const response = await api.post<ApiResponse<LoginResponse>>('/auth/login', {
      email,
      password,
    });
    return response.data.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
}

export async function register(data: RegisterPayload): Promise<RegisterResponse> {
  try {
    const response = await api.post<ApiResponse<RegisterResponse>>('/auth/register', data);
    return response.data.data;
  } catch (error) {
    // Il codice (es. PHONE_ALREADY_USED) serve al form per evidenziare il campo giusto.
    throw Object.assign(new Error(extractErrorMessage(error)), { code: extractErrorCode(error) });
  }
}

export async function updateConsents(data: UpdateConsentsPayload): Promise<UserWithProfile> {
  try {
    const response = await api.patch<ApiResponse<UserWithProfile>>('/auth/me/consents', data);
    return response.data.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
}

export async function completeOnboarding(data: CompleteOnboardingPayload): Promise<UserWithProfile> {
  try {
    const response = await api.post<ApiResponse<UserWithProfile>>('/auth/me/complete-onboarding', data);
    return response.data.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
}

export async function requestPasswordReset(email: string): Promise<void> {
  try {
    await api.post('/auth/forgot-password', { email });
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
}

export async function resetPassword(data: {
  token: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<void> {
  try {
    await api.post('/auth/reset-password', data);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
}

export async function logout(refreshToken: string): Promise<void> {
  await api.post('/auth/logout', { refreshToken });
}

export async function getCurrentUser(): Promise<UserWithProfile> {
  const response = await api.get<ApiResponse<UserWithProfile>>('/auth/me');
  return response.data.data;
}

export async function changePassword(data: {
  newPassword: string;
  confirmPassword: string;
}): Promise<{ tokens: TokenPair }> {
  try {
    const response = await api.post<ApiResponse<{ tokens: TokenPair }>>('/auth/change-password', data);
    return response.data.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
}
